
import { useState, useRef } from "react";
import { toast } from "sonner";
import { R2_FOLDER_NAMES } from "@/lib/constants";
import { supabase } from "@/integrations/supabase/client";

interface UseR2UploadProps {
  folder: string;
  userId?: string;
  onUpload?: (url: string) => Promise<void>;
}

export const useR2Upload = ({ folder, userId, onUpload }: UseR2UploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null); // Added error state
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleThumbnailClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    setPreviewUrl('');
    setError(null); // Clear error on remove
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setError(null); // Reset error state
      // Create a preview of the selected image
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      
      // If onUpload callback is provided, upload the file
      if (file && onUpload) {
        await handleUpload(file);
      }
    } catch (error) {
      console.error("Error handling file change:", error);
      setError("Failed to process file");
      toast.error("Failed to process file");
    }
  };

  const handleUpload = async (file: File): Promise<string> => {
    if (!userId) {
      const errorMsg = "User ID is required for upload";
      setError(errorMsg);
      toast.error(errorMsg);
      return '';
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${folder}/${userId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('profiles')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data: urlData } = await supabase.storage
        .from('profiles')
        .getPublicUrl(fileName);

      if (onUpload && urlData.publicUrl) {
        await onUpload(urlData.publicUrl);
      }

      return urlData.publicUrl;
    } catch (error: any) {
      console.error("Error uploading file:", error);
      const errorMsg = error.message || "Upload failed";
      setError(errorMsg);
      toast.error(errorMsg);
      return '';
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    previewUrl,
    fileInputRef,
    error, // Expose error state
    handleFileChange,
    handleRemove,
    handleThumbnailClick,
    handleUpload
  };
};
